import { useEffect, useState } from 'react';

/**
 * Single-fetch hook backed by GET /api/version. The endpoint already serves
 * version + git_sha + built_at; this PR adds ``getpawtected_env`` and
 * ``self_serve_signup`` so the frontend can gate the invitation-only flow.
 *
 * The fetch is memoised at module scope, so multiple components calling
 * useEnvironment share one network round-trip and one cached payload.
 */

export type GetpawtectedEnv = 'dev' | 'local' | 'staging' | 'prod' | null;

export interface EnvironmentInfo {
  /** Normalised value of the backend's GETPAWTECTED_ENV. */
  env: GetpawtectedEnv;
  /** True when self-serve sign-up is allowed for this deploy. */
  selfServeSignup: boolean;
  /** False until the first fetch completes (success or failure). */
  loaded: boolean;
}

interface VersionPayload {
  version?: string;
  git_sha?: string;
  built_at?: string;
  getpawtected_env?: GetpawtectedEnv;
  self_serve_signup?: boolean;
}

const DEFAULT_INFO: EnvironmentInfo = {
  env: null,
  selfServeSignup: true, // fail open until we know — landing page is unauthenticated
  loaded: false,
};

let cached: EnvironmentInfo | null = null;
let inFlight: Promise<EnvironmentInfo> | null = null;

const resolveApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
    return 'http://localhost:8000/api/';
  }
  return (
    (import.meta as any).env?.VITE_API_URL ||
    'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/'
  );
};

const normaliseEnv = (raw: unknown): GetpawtectedEnv => {
  if (raw === null || raw === undefined) return null;
  const value = String(raw).trim().toLowerCase();
  if (!value) return null;
  if (value === 'dev' || value === 'development') return 'dev';
  if (value === 'stage' || value === 'staging') return 'staging';
  if (value === 'prod' || value === 'production') return 'prod';
  if (value === 'local' || value === 'localhost') return 'local';
  return null;
};

async function fetchEnvironment(): Promise<EnvironmentInfo> {
  if (cached) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const url = `${resolveApiBaseUrl()}version`;
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as VersionPayload;
      const env = normaliseEnv(data.getpawtected_env);
      const info: EnvironmentInfo = {
        env,
        selfServeSignup:
          typeof data.self_serve_signup === 'boolean'
            ? data.self_serve_signup
            : env === null || env === 'dev' || env === 'local',
        loaded: true,
      };
      cached = info;
      return info;
    } catch {
      // On any failure, conservatively assume the most-restrictive answer
      // for production-shaped hostnames so we don't accidentally expose
      // signup CTAs we promised would be hidden.
      const restrictive = typeof window !== 'undefined'
        && /\bprod\b|\bstage\b|\bstaging\b/.test(window.location.hostname);
      const fallback: EnvironmentInfo = {
        env: restrictive ? 'prod' : null,
        selfServeSignup: !restrictive,
        loaded: true,
      };
      cached = fallback;
      return fallback;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export function useEnvironment(): EnvironmentInfo {
  const [info, setInfo] = useState<EnvironmentInfo>(cached ?? DEFAULT_INFO);

  useEffect(() => {
    let cancelled = false;
    fetchEnvironment().then((resolved) => {
      if (!cancelled) setInfo(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return info;
}
